/**
 * Notification settings section for Settings page
 *
 * Three loading states:
 * 1. isError && !settings (no cache): full error + retry
 * 2. isError && settings (cache exists): show cached + warning
 * 3. !isError && !settings && isLoading (loading, no cache): defaults disabled
 */

import { useNotificationSettingsQuery } from '../../hooks/useNotificationSettingsQuery';
import { useUpdateNotificationSettings } from '../../hooks/useUpdateNotificationSettings';
import { AlertCircle, RefreshCw } from 'lucide-react';

export function NotificationSection() {
  const { data: settings, isLoading, isError, refetch } = useNotificationSettingsQuery();
  const updateSettings = useUpdateNotificationSettings();

  // State 1: Error with no cached data — full error
  if (isError && !settings) {
    return (
      <div className="bg-[#1A1F26] rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">NOTIFICATIONS</h2>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <AlertCircle className="text-red-400" size={32} />
          <p className="text-slate-400 text-sm text-center">
            Couldn't load your notification settings.
          </p>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors text-sm font-medium"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Are we in "instant defaults" mode? (loading, no cache yet)
  const isDefaultsMode = !settings && isLoading;

  // Use real settings when available, otherwise hardcoded defaults
  const emailEnabled = settings?.email_enabled ?? true;
  const digestFrequency = settings?.email_digest_frequency ?? 'never';
  const notifyNewFollower = settings?.notify_new_follower ?? true;
  const notifyRecommendation = settings?.notify_recommendation ?? true;
  const notifyListShared = settings?.notify_list_shared ?? true;
  const notifyMilestone = settings?.notify_milestone ?? true;

  // Controls disabled when: defaults mode (write guard), or mutation pending
  const controlsDisabled = isDefaultsMode || updateSettings.isPending;

  const handleEmailToggle = (enabled: boolean) => {
    if (controlsDisabled) return;
    updateSettings.mutate({ email_enabled: enabled });
  };

  const handleDigestChange = (value: 'daily' | 'weekly' | 'never') => {
    if (controlsDisabled) return;
    updateSettings.mutate({ email_digest_frequency: value });
  };

  const handleNotifyToggle = (key: string, enabled: boolean) => {
    if (controlsDisabled) return;
    updateSettings.mutate({ [key]: enabled } as Record<string, boolean>);
  };

  const emailDisabled = !emailEnabled;

  return (
    <div className="bg-[#1A1F26] rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4">NOTIFICATIONS</h2>

      {/* State 2: Error with cached data — show warning */}
      {isError && settings && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-yellow-500/10 rounded-lg">
          <AlertCircle className="text-yellow-400 shrink-0" size={14} />
          <span className="text-xs text-yellow-400">Couldn't refresh — showing cached settings</span>
          <button
            onClick={() => refetch()}
            className="ml-auto text-xs text-yellow-400 hover:text-yellow-300 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* State 3: Loading defaults mode — helper label */}
      {isDefaultsMode && (
        <p className="text-xs text-slate-500 mb-4">Loading your saved settings...</p>
      )}

      <div className={`space-y-4 ${isDefaultsMode ? 'opacity-50' : ''}`}>
        {/* Global Email Toggle */}
        <div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">Email Notifications</div>
              <div className="text-sm text-slate-400">Receive emails from Filmple</div>
            </div>
            <button
              onClick={() => handleEmailToggle(!emailEnabled)}
              disabled={controlsDisabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                emailEnabled ? 'bg-teal-500' : 'bg-slate-600'
              } ${controlsDisabled ? 'cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Digest Frequency */}
        <div className={`pt-4 border-t border-white/[0.06] ${emailDisabled ? 'opacity-50' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-white font-medium">Email Digest</div>
              <div className="text-sm text-slate-400">How often to receive activity digests</div>
            </div>
            <select
              value={digestFrequency}
              onChange={(e) => handleDigestChange(e.target.value as 'daily' | 'weekly' | 'never')}
              className="px-3 py-1.5 bg-[#2A2F36] text-white rounded-lg border border-white/[0.08] focus:outline-none focus:ring-2 focus:ring-teal-500"
              disabled={emailDisabled || controlsDisabled}
            >
              <option value="never">Never</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
        </div>

        {/* Per-type Toggles */}
        <div className={`pt-4 border-t border-white/[0.06] ${emailDisabled ? 'opacity-50' : ''}`}>
          <div className="text-sm text-slate-400 mb-3">Notify me about</div>
          <div className="space-y-3">
            <ToggleRow
              label="New Followers"
              description="When someone follows you"
              enabled={notifyNewFollower}
              disabled={emailDisabled || controlsDisabled}
              onToggle={(v) => handleNotifyToggle('notify_new_follower', v)}
            />
            <ToggleRow
              label="Recommendations"
              description="When someone sends you a recommendation"
              enabled={notifyRecommendation}
              disabled={emailDisabled || controlsDisabled}
              onToggle={(v) => handleNotifyToggle('notify_recommendation', v)}
            />
            <ToggleRow
              label="Lists Shared"
              description="When a list is shared with you"
              enabled={notifyListShared}
              disabled={emailDisabled || controlsDisabled}
              onToggle={(v) => handleNotifyToggle('notify_list_shared', v)}
            />
            <ToggleRow
              label="Milestones"
              description="When you reach a milestone"
              enabled={notifyMilestone}
              disabled={emailDisabled || controlsDisabled}
              onToggle={(v) => handleNotifyToggle('notify_milestone', v)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  enabled,
  disabled,
  onToggle,
}: {
  label: string;
  description: string;
  enabled: boolean;
  disabled: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-white text-sm font-medium">{label}</div>
        <div className="text-xs text-slate-400">{description}</div>
      </div>
      <button
        onClick={() => onToggle(!enabled)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-teal-500' : 'bg-slate-600'
        } ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}
